import React from 'react';
import { LoadingError } from 'components/loadingError';
import { useIsMounted } from 'utils';
import {
  useJobsiteUpdateFormSubmissionMutation,
  GetFormSubmissionJobsiteWorkersDocument,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import {
  CustomFormInputs,
  EditFormData,
  FormSubmissionTabProps,
} from 'containers/jobsiteFormSubmission/types';
import { Form, FormOnSubmit } from 'components/form';
import { AuthContext } from 'auth';
import { getGraphQLError } from 'utils/error';
import {
  useDependencies,
  useAvailableJobsiteWorkerOptions,
  useDynCtx,
} from 'containers/jobsiteFormSubmission/utils';
import {
  useJobsiteContractorsOptions,
  useJobsiteFormsOptions,
} from 'containers/forms/modals/utils';
import {
  getContextSelectOptions,
  getDefaultValues,
  getEvalContext,
  getFormInputsHook,
  getJobsiteFormSubmissionUpdateInput,
} from './FormSubmissionEdit.forms';

export const FormSubmissionEdit = React.forwardRef<
  HTMLFormElement,
  FormSubmissionTabProps
>(function FormSubmissionEdit(props, ref): React.ReactElement {
  const {
    jobsiteFormSubmission,
    loading,
    onIsDirtyChange,
    refetchJobsiteFormSubmission,
    isSaving,
    setIsSaving,
    setTabsDefinition,
    deferredSubmission,
  } = props;

  const formContent = jobsiteFormSubmission?.jobsiteForm.form.content;

  const {
    inputs: inputsExpression,
    defaultValues: defaultValuesExpression,
    updateInputs: updateInputsExpression,
    dependencies: dependenciesExpressions,
    extraDataFields,
  } = formContent?.edit ?? {};

  const isMounted = useIsMounted();
  const { currentUser: user } = React.useContext(AuthContext);
  const [jobsiteUpdateFormSubmission] = useJobsiteUpdateFormSubmissionMutation({
    refetchQueries: [GetFormSubmissionJobsiteWorkersDocument],
  });

  const { availableJobsiteWorkerOptions } = useAvailableJobsiteWorkerOptions({
    jobsiteFormSubmission,
    skip:
      !(inputsExpression as string)?.includes(
        'ctx.options.availableJobsiteWorkers',
      ) &&
      !(defaultValuesExpression as string)?.includes(
        'ctx.options.availableJobsiteWorkers',
      ),
  });

  const dependencies = useDependencies(dependenciesExpressions);
  const dynCtx = useDynCtx(formContent);
  const jobsiteFormsOptions = useJobsiteFormsOptions(
    jobsiteFormSubmission?.jobsiteForm.form.key,
  );

  const contractorsOptions = useJobsiteContractorsOptions({
    jobsiteId: jobsiteFormSubmission?.jobsiteForm.jobsite.jobsiteId,
    skip:
      !(inputsExpression as string)?.includes('ctx.options.contractors') &&
      !(defaultValuesExpression as string)?.includes('ctx.options.contractors'),
  });

  const evalContext = React.useMemo(() => {
    return (
      dependencies &&
      availableJobsiteWorkerOptions &&
      getEvalContext({
        dependencies,
        dynCtx,
        user,
        jobsiteFormSubmission,
        options: getContextSelectOptions({
          jobsiteForms: jobsiteFormsOptions,
          availableJobsiteWorkers: availableJobsiteWorkerOptions,
          contractors: contractorsOptions,
        }),
      })
    );
  }, [
    dependencies,
    dynCtx,
    user,
    jobsiteFormSubmission,
    jobsiteFormsOptions,
    availableJobsiteWorkerOptions,
  ]);

  const defaultValues = React.useMemo(() => {
    return getDefaultValues({ jobsiteFormSubmission, evalContext });
  }, [jobsiteFormSubmission, evalContext]);

  const inputs = React.useMemo(() => {
    return getFormInputsHook({
      jobsiteFormSubmission,
      defaultValues,
      evalContext,
      setTabsDefinition,
    });
  }, [jobsiteFormSubmission, defaultValues, evalContext, setTabsDefinition]);

  const onSubmit: FormOnSubmit<CustomFormInputs<EditFormData>> = async (
    data,
    event,
    dirtyFields,
    form,
  ): Promise<void> => {
    if (isSaving) {
      AlertService.alert('warn', 'Saving in progress!', '');
      return;
    }
    setIsSaving(true);

    try {
      const input = await getJobsiteFormSubmissionUpdateInput({
        jobsiteFormSubmission,
        evalContext: { ctx: { ...evalContext.ctx, form } },
        updateInputsExpression,
        extraDataFields,
        defaultValues,
        data,
        dirtyFields,
      });
      await jobsiteUpdateFormSubmission({ variables: { input } });

      AlertService.alert(
        'success',
        'Success',
        'Form submission successfully updated',
      );
      if (isMounted()) {
        refetchJobsiteFormSubmission();
      }

      deferredSubmission.resolve(true);
    } catch (error) {
      event.preventDefault();
      deferredSubmission.reject(error);
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  if (loading || !inputs || !defaultValues) {
    return <LoadingError loading />;
  }

  return (
    <Form
      ref={ref}
      inputs={inputs}
      onSubmit={onSubmit}
      onIsDirtyChange={onIsDirtyChange}
      defaultValues={defaultValues}
      className="odin-pr-3"
      inputsContainerClassName="odin-space-y-5 sm:odin-space-y-9 odin-divide-y odin-divide-gray-200 odin-pb-9"
    />
  );
});
